import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useUser } from '@/shared/hooks/useAuth';
import {
  Plus, TrendingUp, CornerDownRight, X, Sliders, ShieldAlert,
  Sparkles, Check, Calculator, RefreshCw, FileText
} from 'lucide-react';
import toast from 'react-hot-toast';

interface IncentiveTarget {
  _id: string;
  employeeId: { _id: string; display_name: string; department?: string; username?: string };
  period: string;
  targetAmount: number;
  revenueGenerated: number;
  achievedPercent: number;
  incentivePercent: number;
  incentiveAmount: number;
  assignedBy: { _id: string; display_name: string };
  source: string;
  status: string;
  carriedForwardAmount?: number;
  carriedFromPeriod?: string | null;
  pendingCarryForward?: { sourcePeriod: string; shortfallAmount: number } | null;
}

export interface IncentivePolicy {
  gstDeductionEnabled: boolean;
  gstPercent: number;
  gstCalculation: 'inclusive' | 'flat';
  calcMode: 'excess_then_gst' | 'excess_over_target' | 'total_revenue';
  defaultIncentiveRate: number;
  payoutSchedule: 'quarterly_4th_month' | 'monthly';
  payoutMonths?: number[];
  excludedUsernames: string[];
}

function useIncentivePolicy() {
  return useQuery({
    queryKey: ['hr', 'incentive-policy'],
    queryFn: async () => {
      const { data } = await api.get('/api/hr-incentives/policy');
      return data as { policy: IncentivePolicy; defaults: IncentivePolicy };
    },
  });
}

function useUpdateIncentivePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<IncentivePolicy> & { recomputeTargets?: boolean }) => {
      const { data } = await api.put('/api/hr-incentives/policy', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'incentive-policy'] });
      qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] });
    },
  });
}

function useSyncInvoices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (period: string) => {
      const { data } = await api.post('/api/hr-incentives/sync-invoices', { period });
      return data;
    },
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] });
      toast.success(`Synced revenue from ${data.matchedInvoices || 0} final invoice(s). Quotations excluded.`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to sync from invoices.'),
  });
}

function useTargets(period?: string) {
  return useQuery({
    queryKey: ['hr', 'incentive-targets', period],
    queryFn: async () => {
      const p = period ? `?period=${period}` : '';
      const { data } = await api.get(`/api/hr-incentives/targets${p}`);
      return data as { data: IncentiveTarget[]; summary: { totalTarget: number; totalRevenue: number; totalIncentive: number } };
    },
  });
}

function useMyTargets() {
  return useQuery({
    queryKey: ['hr', 'my-targets'],
    queryFn: async () => { const { data } = await api.get('/api/hr-incentives/targets/mine'); return data as { data: IncentiveTarget[] }; },
  });
}

function useEmployeeList() {
  return useQuery({
    queryKey: ['hr', 'employees-list'],
    queryFn: async () => {
      const { data } = await api.get<{ users?: { id: string; display_name: string; username?: string; department?: string; exclude_from_leaderboard?: boolean; exclude_from_incentive?: boolean }[] }>('/api/users');
      return (data?.users ?? []).map((u) => ({
        _id: u.id,
        display_name: u.display_name,
        username: u.username || '',
        department: u.department,
        exclude_from_leaderboard: u.exclude_from_leaderboard,
        exclude_from_incentive: u.exclude_from_incentive,
      }));
    },
  });
}

function useAssignTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { employeeId: string; period: string; targetAmount: number; incentivePercent: number; source?: string }) => {
      const { data } = await api.post('/api/hr-incentives/targets', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] }),
  });
}

function useUpdateRevenue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, revenueGenerated }: { id: string; revenueGenerated: number }) => {
      const { data } = await api.patch(`/api/hr-incentives/targets/${id}/revenue`, { revenueGenerated });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] }),
  });
}

function useCarryForward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, accept }: { id: string; accept: boolean }) => {
      const { data } = await api.post(`/api/hr-incentives/targets/${id}/carry-forward`, { accept });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] }),
  });
}

function useClearCarryForward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/api/hr-incentives/targets/${id}/clear-carry-forward`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] }),
  });
}

function getCurrentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function IncentivesPage() {
  const user = useUser();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin' || (user?.department === 'hr' && ['manager', 'team_lead'].includes(user?.role ?? ''));

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Incentives & Targets</h1>
        <p className="text-sm text-muted-foreground mt-1">Assign revenue targets, track achievement, and calculate incentive payouts based on performance.</p>
      </div>
      {isAdmin ? <AdminIncentiveView /> : <EmployeeIncentiveView />}
    </div>
  );
}

export function AdminIncentiveView() {
  const user = useUser();
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const [period, setPeriod] = useState(getCurrentPeriod());
  const { data, isLoading } = useTargets(period);
  const { data: employees } = useEmployeeList();
  const { data: policyData } = useIncentivePolicy();
  const updatePolicyMut = useUpdateIncentivePolicy();

  const assignMut = useAssignTarget();
  const updateRevMut = useUpdateRevenue();
  const carryForwardMut = useCarryForward();
  const clearCarryForwardMut = useClearCarryForward();
  const syncInvoicesMut = useSyncInvoices();

  const [showNew, setShowNew] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [incentivePercent, setIncentivePercent] = useState('1');

  // Policy Form State
  const [gstEnabled, setGstEnabled] = useState(true);
  const [gstPercent, setGstPercent] = useState('18');
  const [gstCalculation, setGstCalculation] = useState<'inclusive' | 'flat'>('inclusive');
  const [calcMode, setCalcMode] = useState<'excess_over_target' | 'total_revenue'>('excess_over_target');
  const [defaultRate, setDefaultRate] = useState('1');
  const [payoutSchedule, setPayoutSchedule] = useState<'quarterly_4th_month' | 'monthly'>('quarterly_4th_month');
  const [excludedList, setExcludedList] = useState<string[]>(['bdm6']);
  const [recomputeTargets, setRecomputeTargets] = useState(true);

  // Sync policy form with backend data
  useEffect(() => {
    if (policyData?.policy) {
      const p = policyData.policy;
      setGstEnabled(p.gstDeductionEnabled !== false);
      setGstPercent(String(p.gstPercent ?? 18));
      setGstCalculation(p.gstCalculation || 'inclusive');
      setCalcMode(p.calcMode || 'excess_over_target');
      setDefaultRate(String(p.defaultIncentiveRate ?? 1));
      setPayoutSchedule(p.payoutSchedule || 'quarterly_4th_month');
      setExcludedList(Array.isArray(p.excludedUsernames) ? p.excludedUsernames : ['bdm6']);
    }
  }, [policyData]);

  const handleAssign = async () => {
    if (!employeeId || !targetAmount || incentivePercent === '') {
      toast.error('All fields are required.');
      return;
    }
    try {
      await assignMut.mutateAsync({
        employeeId,
        period,
        targetAmount: Number(targetAmount),
        incentivePercent: Number(incentivePercent),
        source: 'manual',
      });
      toast.success('Target assigned.');
      setShowNew(false);
      setEmployeeId('');
      setTargetAmount('');
      setIncentivePercent('1');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed.');
    }
  };

  const handleSavePolicy = async () => {
    try {
      await updatePolicyMut.mutateAsync({
        gstDeductionEnabled: gstEnabled,
        gstPercent: Number(gstPercent) || 0,
        gstCalculation,
        calcMode,
        defaultIncentiveRate: Number(defaultRate) || 1,
        payoutSchedule,
        excludedUsernames: excludedList,
        recomputeTargets,
      });
      toast.success('Incentive policy & rules saved successfully!');
      setShowPolicyModal(false);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save incentive policy.');
    }
  };

  const toggleExcludeUser = (username: string) => {
    if (!username) return;
    setExcludedList((prev) =>
      prev.includes(username) ? prev.filter((u) => u !== username) : [...prev, username]
    );
  };

  if (isLoading) return <Skeleton className="h-[400px]" />;
  const targets = data?.data || [];
  const summary = data?.summary || { totalTarget: 0, totalRevenue: 0, totalIncentive: 0 };
  const currentPolicy = policyData?.policy;

  return (
    <>
      {/* Assign Target Modal */}
      <Modal
        open={showNew}
        onClose={() => setShowNew(false)}
        title="Assign Target"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={assignMut.isPending}>Assign</Button>
          </div>
        }
      >
        <div className="space-y-3 py-3">
          <Select
            name="t-employee"
            label="Employee (BDE / Staff)"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            options={[
              { value: '', label: 'Select Employee...' },
              ...(employees || []).map((e) => ({
                value: e._id,
                label: `${e.display_name} ${e.username === 'bdm6' ? '(Rukmani - Client Coordinator)' : `(${e.department || '-'})`}`,
              })),
            ]}
          />
          <Input
            name="t-target"
            label={`Target Amount for ${period} (₹)`}
            type="number"
            min="0"
            value={targetAmount}
            onChange={(e) => setTargetAmount(e.target.value)}
            placeholder="e.g. 1500000 (15 Lakh)"
          />
          <Input
            name="t-percent"
            label="Incentive Rate % (Default 1% = 0.01 multiplier)"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={incentivePercent}
            onChange={(e) => setIncentivePercent(e.target.value)}
            placeholder="e.g. 1"
          />
          <div className="rounded bg-muted/40 p-2.5 text-xs text-muted-foreground space-y-1">
            <p className="font-semibold text-foreground flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" /> RBM Rule Active:
            </p>
            <p>
              Incentive = (Revenue Generated − GST − Target) × {incentivePercent || '1'}%.
              Shortfalls accrue, and 3 months combined incentive is credited in the 4th month's Salary Slip.
            </p>
          </div>
        </div>
      </Modal>

      {/* SuperAdmin Incentive Rules & Policy Modal */}
      <Modal
        open={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        title="SuperAdmin Incentive Rules & Payout Policy"
        footer={
          <div className="flex justify-between items-center w-full">
            <div className="text-xs text-muted-foreground">Changes take effect immediately across all calculations.</div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setShowPolicyModal(false)}>Cancel</Button>
              <Button onClick={handleSavePolicy} disabled={updatePolicyMut.isPending}>
                <Check className="h-4 w-4 mr-1.5" /> Save & Apply Rules
              </Button>
            </div>
          </div>
        }
      >
        <div className="space-y-4 py-2 text-sm max-h-[75vh] overflow-y-auto pr-1">
          {/* Section 1: GST Deduction */}
          <div className="rounded-lg border border-border p-3.5 space-y-3 bg-muted/20">
            <div className="flex items-center justify-between">
              <div>
                <label className="font-semibold text-foreground flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={gstEnabled}
                    onChange={(e) => setGstEnabled(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  Deduct GST from Revenue Before Calculating Incentive
                </label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Gross revenue will have GST deducted first so incentive is only paid on net company revenue.
                </p>
              </div>
            </div>

            {gstEnabled && (
              <div className="grid grid-cols-2 gap-3 pt-1">
                <Input
                  label="GST Percentage (%)"
                  type="number"
                  min="0"
                  max="100"
                  value={gstPercent}
                  onChange={(e) => setGstPercent(e.target.value)}
                  placeholder="18"
                />
                <div>
                  <label className="block text-xs font-medium text-foreground mb-1.5">Tax Calculation Method</label>
                  <select
                    className="w-full text-sm rounded-md border border-input bg-background px-3 py-2"
                    value={gstCalculation}
                    onChange={(e: any) => setGstCalculation(e.target.value)}
                  >
                    <option value="inclusive">Inclusive GST (Gross ÷ 1.18) [Standard]</option>
                    <option value="flat">Flat Deduct (Gross − 18%)</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Calculation Mode */}
          <div className="rounded-lg border border-border p-3.5 space-y-3 bg-muted/20">
            <div>
              <span className="font-semibold text-foreground block">Target Threshold & Eligibility</span>
              <p className="text-xs text-muted-foreground mt-0.5">
                Determine which revenue portion qualifies for the incentive multiplier.
              </p>
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-2.5 p-2 rounded border border-primary/60 bg-primary/5 hover:bg-primary/10 cursor-pointer">
                <input
                  type="radio"
                  name="calcMode"
                  value="excess_then_gst"
                  checked={calcMode === 'excess_then_gst'}
                  onChange={() => setCalcMode('excess_then_gst')}
                  className="mt-0.5 h-4 w-4 text-primary focus:ring-primary"
                />
                <div>
                  <span className="font-medium text-foreground block">
                    (Revenue − Target) ÷ 1.18 × Rate% (RBM Standard Rule)
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Target (e.g. ₹25L) subtracted from gross invoiced revenue (e.g. ₹55L) to get ₹30L, then divided by 1.18 to exclude tax, multiplied by 0.01 (1%) = <strong>₹25,424</strong>.
                  </span>
                </div>
              </label>

              <label className="flex items-start gap-2.5 p-2 rounded border border-border/80 bg-background hover:bg-muted/40 cursor-pointer">
                <input
                  type="radio"
                  name="calcMode"
                  value="excess_over_target"
                  checked={calcMode === 'excess_over_target'}
                  onChange={() => setCalcMode('excess_over_target')}
                  className="mt-0.5 h-4 w-4 text-primary focus:ring-primary"
                />
                <div>
                  <span className="font-medium text-foreground block">
                    (Revenue ÷ 1.18 − Target) × Rate%
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Revenue converted to Ex-GST first (e.g. ₹55L ÷ 1.18 = ₹46.61L), then ₹25L target subtracted = ₹21.61L × 0.01 = <strong>₹21,610</strong>.
                  </span>
                </div>
              </label>

              <label className="flex items-start gap-2.5 p-2 rounded border border-border/80 bg-background hover:bg-muted/40 cursor-pointer">
                <input
                  type="radio"
                  name="calcMode"
                  value="total_revenue"
                  checked={calcMode === 'total_revenue'}
                  onChange={() => setCalcMode('total_revenue')}
                  className="mt-0.5 h-4 w-4 text-primary focus:ring-primary"
                />
                <div>
                  <span className="font-medium text-foreground block">
                    Total Achieved Revenue
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Incentive is calculated on full net revenue: Revenue Ex-GST × Rate%.
                  </span>
                </div>
              </label>
            </div>

            <div className="pt-1">
              <Input
                label="Default Incentive Multiplier %"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={defaultRate}
                onChange={(e) => setDefaultRate(e.target.value)}
                placeholder="1.0"
              />
              <p className="text-xs text-muted-foreground mt-1">
                E.g. 1.0% corresponds to 0.01 multiplier as requested for RBM reps.
              </p>
            </div>
          </div>

          {/* Section 3: Payout Schedule (Quarterly 4th Month vs Monthly) */}
          <div className="rounded-lg border border-border p-3.5 space-y-3 bg-muted/20">
            <div>
              <span className="font-semibold text-foreground block">Salary Slip Payout Schedule</span>
              <p className="text-xs text-muted-foreground mt-0.5">
                When does the earned incentive get added to the employee's Salary Slip?
              </p>
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-2.5 p-2 rounded border border-border/80 bg-background hover:bg-muted/40 cursor-pointer">
                <input
                  type="radio"
                  name="payoutSchedule"
                  value="quarterly_4th_month"
                  checked={payoutSchedule === 'quarterly_4th_month'}
                  onChange={() => setPayoutSchedule('quarterly_4th_month')}
                  className="mt-0.5 h-4 w-4 text-primary focus:ring-primary"
                />
                <div>
                  <span className="font-medium text-foreground block">
                    Quarterly Combined: Added in 4th Month Salary Slip (RBM Rule)
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Accrues monthly for 3 months, combined together, and credited in the 4th month's Salary Slip:
                    <br />• <strong>July Slip</strong>: Pays April + May + June (Q1)
                    <br />• <strong>October Slip</strong>: Pays July + August + September (Q2)
                    <br />• <strong>January Slip</strong>: Pays October + November + December (Q3)
                    <br />• <strong>April Slip</strong>: Pays January + February + March (Q4)
                  </span>
                </div>
              </label>

              <label className="flex items-start gap-2.5 p-2 rounded border border-border/80 bg-background hover:bg-muted/40 cursor-pointer">
                <input
                  type="radio"
                  name="payoutSchedule"
                  value="monthly"
                  checked={payoutSchedule === 'monthly'}
                  onChange={() => setPayoutSchedule('monthly')}
                  className="mt-0.5 h-4 w-4 text-primary focus:ring-primary"
                />
                <div>
                  <span className="font-medium text-foreground block">
                    Monthly Salary Slip Payout
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Each month's earned incentive is added directly to that month's salary slip.
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Section 4: Exclude from Leaderboards & Incentives (Rukmani rule) */}
          <div className="rounded-lg border border-amber-200/80 bg-amber-50/50 p-3.5 space-y-3 dark:bg-amber-950/20 dark:border-amber-900/50">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-amber-600" />
              <span className="font-semibold text-amber-950 dark:text-amber-200">
                Staff Excluded from Leaderboards & Incentive Pool
              </span>
            </div>
            <p className="text-xs text-amber-900/80 dark:text-amber-300/80">
              Employees who manage SuperAdmin clients (e.g. <strong>Rukmani / bdm6</strong>) are assigned targets for internal performance monitoring, but are completely hidden from public rep leaderboards and excluded from receiving sales incentives on their salary slip.
            </p>

            <div className="space-y-2">
              <span className="text-xs font-semibold uppercase text-muted-foreground block">Currently Excluded Staff:</span>
              <div className="flex flex-wrap gap-1.5">
                {excludedList.map((uname) => {
                  const emp = (employees || []).find((e) => e.username === uname);
                  return (
                    <span
                      key={uname}
                      className="inline-flex items-center gap-1.5 rounded-full bg-amber-200/80 px-2.5 py-0.5 text-xs font-semibold text-amber-900 border border-amber-300 dark:bg-amber-900 dark:text-amber-100"
                    >
                      {emp ? emp.display_name : uname} ({uname})
                      <button
                        type="button"
                        onClick={() => toggleExcludeUser(uname)}
                        className="hover:text-red-700"
                        title="Remove exclusion"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>

              <div className="pt-2">
                <span className="text-xs text-muted-foreground block mb-1">Click to toggle exclusion for other employees:</span>
                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-1.5 border border-border rounded bg-background">
                  {(employees || []).map((e) => {
                    const isEx = excludedList.includes(e.username);
                    return (
                      <button
                        key={e._id}
                        type="button"
                        onClick={() => toggleExcludeUser(e.username)}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${
                          isEx
                            ? 'bg-amber-100 text-amber-900 border-amber-300 font-semibold'
                            : 'bg-muted/40 text-muted-foreground border-transparent hover:bg-muted'
                        }`}
                      >
                        {e.display_name} ({e.username})
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Interactive Formula Preview */}
          <div className="rounded-lg border border-purple-200 bg-purple-50/60 p-3 text-xs text-purple-950 space-y-1.5 dark:bg-purple-950/20 dark:border-purple-900/50 dark:text-purple-200">
            <div className="flex items-center gap-1.5 font-semibold">
              <Calculator className="h-4 w-4 text-purple-600" />
              <span>Live Rule Calculation (Example: Target ₹25 Lakh, Achieved ₹55 Lakh Including Tax):</span>
            </div>
            {calcMode === 'excess_then_gst' && (
              <div className="grid grid-cols-2 gap-2 text-[12px] pt-1">
                <div>
                  <span className="text-muted-foreground">1. Invoiced (Tax Incl.):</span> ₹55,00,000
                  <br />
                  <span className="text-muted-foreground">2. Less Target ₹25L:</span> ₹30,00,000 surplus
                </div>
                <div>
                  <span className="text-muted-foreground">3. Divided by 1.18 (Ex-Tax):</span> ₹25,42,373 net
                  <br />
                  <span className="text-muted-foreground">4. Incentive ({defaultRate}% = 0.01):</span> <strong className="text-purple-700 dark:text-purple-300 font-bold">₹25,424</strong>
                </div>
              </div>
            )}
            {calcMode === 'excess_over_target' && (
              <div className="grid grid-cols-2 gap-2 text-[12px] pt-1">
                <div>
                  <span className="text-muted-foreground">1. Invoiced (Tax Incl.):</span> ₹55,00,000
                  <br />
                  <span className="text-muted-foreground">2. Divided by 1.18 (Ex-Tax):</span> ₹46,61,017
                </div>
                <div>
                  <span className="text-muted-foreground">3. Less Target ₹25L:</span> ₹21,61,017 surplus
                  <br />
                  <span className="text-muted-foreground">4. Incentive ({defaultRate}% = 0.01):</span> <strong className="text-purple-700 dark:text-purple-300 font-bold">₹21,610</strong>
                </div>
              </div>
            )}
            {calcMode === 'total_revenue' && (
              <div className="grid grid-cols-2 gap-2 text-[12px] pt-1">
                <div>
                  <span className="text-muted-foreground">1. Invoiced (Tax Incl.):</span> ₹55,00,000
                  <br />
                  <span className="text-muted-foreground">2. Divided by 1.18 (Ex-Tax):</span> ₹46,61,017
                </div>
                <div>
                  <span className="text-muted-foreground">3. Full Ex-Tax Multiplier:</span> ₹46,61,017 × 0.01
                  <br />
                  <span className="text-muted-foreground">4. Incentive ({defaultRate}%):</span> <strong className="text-purple-700 dark:text-purple-300 font-bold">₹46,610</strong>
                </div>
              </div>
            )}
            <div className="pt-1 text-[11px] text-muted-foreground">
              Payout: Accrues across 3 months, combined and credited into employee's 4th-month Salary Slip!
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs font-medium text-foreground cursor-pointer pt-1">
            <input
              type="checkbox"
              checked={recomputeTargets}
              onChange={(e) => setRecomputeTargets(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            Recalculate all active targets for current period immediately with these new rules
          </label>
        </div>
      </Modal>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-lg border p-4 bg-info/10">
          <p className="text-xs text-muted-foreground">Total Target</p>
          <p className="text-xl font-bold">₹{summary.totalTarget.toLocaleString('en-IN')}</p>
        </div>
        <div className="rounded-lg border p-4 bg-success/10">
          <p className="text-xs text-muted-foreground">Revenue Generated</p>
          <p className="text-xl font-bold text-success-text">₹{summary.totalRevenue.toLocaleString('en-IN')}</p>
        </div>
        <div className="rounded-lg border p-4 bg-purple-50">
          <p className="text-xs text-muted-foreground">Incentive Payout</p>
          <p className="text-xl font-bold text-purple-700">₹{summary.totalIncentive.toLocaleString('en-IN')}</p>
        </div>
        <div className="rounded-lg border p-4 bg-amber-50/60 flex flex-col justify-between">
          <div>
            <p className="text-xs text-muted-foreground">Active Policy</p>
            <p className="text-xs font-bold text-amber-900 mt-1">
              {currentPolicy?.calcMode === 'excess_over_target' ? 'Surplus Ex-GST × Rate' : 'Total Revenue × Rate'}
            </p>
          </div>
          <p className="text-[11px] text-amber-800/80">
            {currentPolicy?.payoutSchedule === 'quarterly_4th_month' ? 'Combined 3 Months → 4th Month Slip' : 'Monthly Salary Slip'}
          </p>
        </div>
      </div>

      {/* Period selector + action buttons */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Input
            name="period"
            label="Period"
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-44"
          />
          <Button size="sm" onClick={() => setShowNew(true)} className="mt-5">
            <Plus className="h-4 w-4 mr-1" /> Assign Target
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={syncInvoicesMut.isPending}
            onClick={() => syncInvoicesMut.mutate(period)}
            className="mt-5"
            title="Sync employee revenue directly from posted Final Tax Invoices"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncInvoicesMut.isPending ? 'animate-spin' : ''}`} />
            Sync from Invoices
          </Button>
          <div className="mt-5 flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/40 px-3 py-1.5 rounded-md border border-border/60">
            <FileText className="h-3.5 w-3.5 text-blue-500" />
            <span>Revenue Source: <strong>Final Tax Invoices Only</strong> (Quotations Excluded)</span>
          </div>
        </div>

        {isSuperAdmin && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowPolicyModal(true)}
            className="border-primary/40 text-primary hover:bg-primary/5 mt-5"
          >
            <Sliders className="h-4 w-4 mr-1.5" /> Incentive Rules & Policy (SuperAdmin)
          </Button>
        )}
      </div>

      {/* Targets Table */}
      <Card className="border-none shadow-sm">
        <CardHeader title="Employee Targets & Achievement" />
        <CardBody className="p-0">
          <div className="overflow-auto max-h-[450px]">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr>
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3 text-right">Target</th>
                  <th className="px-4 py-3 text-right">Revenue</th>
                  <th className="px-4 py-3 text-right">Achievement</th>
                  <th className="px-4 py-3 text-right">Incentive %</th>
                  <th className="px-4 py-3 text-right">Payout</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {targets.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                      No targets assigned for this period.
                    </td>
                  </tr>
                )}
                {targets.map((t) => {
                  const emp = t.employeeId;
                  const isExcluded =
                    emp?.username === 'bdm6' ||
                    (Array.isArray(currentPolicy?.excludedUsernames) && currentPolicy?.excludedUsernames.includes(emp?.username || ''));

                  return (
                    <tr key={t._id} className="hover:bg-muted/20 align-top">
                      <td className="px-4 py-3 font-medium">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{emp?.display_name || '-'}</span>
                          {isExcluded && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold border border-amber-300">
                              Client Coordinator (Excluded from Leaderboard & Incentives)
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div>₹{t.targetAmount?.toLocaleString('en-IN')}</div>
                        {!!t.carriedForwardAmount && (
                          <div className="mt-0.5 flex items-center justify-end gap-1 text-[11px] text-warning-text">
                            <CornerDownRight className="h-3 w-3" />
                            +₹{t.carriedForwardAmount.toLocaleString('en-IN')} carried from {t.carriedFromPeriod}
                            <button
                              type="button"
                              title="Clear carried-forward amount"
                              className="text-muted-foreground hover:text-danger-text"
                              onClick={() =>
                                clearCarryForwardMut
                                  .mutateAsync(t._id)
                                  .then(() => toast.success('Carry-forward cleared.'))
                                  .catch(() => toast.error('Failed.'))
                              }
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-success-text">
                        ₹{t.revenueGenerated?.toLocaleString('en-IN')}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`font-bold ${
                            t.achievedPercent >= 100
                              ? 'text-success-text'
                              : t.achievedPercent >= 70
                              ? 'text-warning-text'
                              : 'text-danger-text'
                          }`}
                        >
                          {t.achievedPercent?.toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isExcluded ? '0%' : `${t.incentivePercent}%`}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-purple-700">
                        {isExcluded ? (
                          <span className="text-muted-foreground text-xs italic">₹0 (Excluded)</span>
                        ) : (
                          `₹${t.incentiveAmount?.toLocaleString('en-IN')}`
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const rev = prompt(
                                `Enter current revenue for ${t.employeeId?.display_name}:`,
                                String(t.revenueGenerated || 0)
                              );
                              if (rev != null)
                                updateRevMut
                                  .mutateAsync({ id: t._id, revenueGenerated: Number(rev) })
                                  .then(() => toast.success('Revenue updated.'))
                                  .catch(() => toast.error('Failed.'));
                            }}
                          >
                            <TrendingUp className="h-3.5 w-3.5" />
                          </Button>
                          {t.pendingCarryForward && (
                            <div className="rounded-md border border-warning/25 bg-warning/10 px-2 py-1.5 text-[11px] leading-tight">
                              <p className="text-warning-text">
                                {t.employeeId?.display_name} missed {t.pendingCarryForward.sourcePeriod} by ₹
                                {t.pendingCarryForward.shortfallAmount.toLocaleString('en-IN')}.
                              </p>
                              <div className="mt-1 flex gap-1.5">
                                <button
                                  type="button"
                                  className="rounded bg-warning px-2 py-0.5 font-medium text-white hover:bg-warning"
                                  onClick={() =>
                                    carryForwardMut
                                      .mutateAsync({ id: t._id, accept: true })
                                      .then(() => toast.success('Carried forward.'))
                                      .catch((err) =>
                                        toast.error(err?.response?.data?.error || 'Failed.')
                                      )
                                  }
                                >
                                  Carry forward
                                </button>
                                <button
                                  type="button"
                                  className="rounded border border-warning/25 px-2 py-0.5 font-medium text-warning-text hover:bg-warning/10"
                                  onClick={() =>
                                    carryForwardMut
                                      .mutateAsync({ id: t._id, accept: false })
                                      .then(() => toast.success('Declined — shortfall absorbed.'))
                                      .catch((err) =>
                                        toast.error(err?.response?.data?.error || 'Failed.')
                                      )
                                  }
                                >
                                  Decline
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

export function EmployeeIncentiveView() {
  const { data, isLoading } = useMyTargets();
  if (isLoading) return <Skeleton className="h-[300px]" />;
  const targets = data?.data || [];

  return (
    <Card className="border-none shadow-sm">
      <CardHeader title="My Targets & Incentives" />
      <CardBody className="p-0">
        <div className="overflow-auto max-h-[500px]">
          {targets.length === 0 ? (
            <p className="px-4 py-8 text-center text-muted-foreground">No targets assigned to you yet.</p>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr>
                  <th className="px-4 py-3">Period</th>
                  <th className="px-4 py-3 text-right">Target</th>
                  <th className="px-4 py-3 text-right">Revenue</th>
                  <th className="px-4 py-3 text-right">Achievement</th>
                  <th className="px-4 py-3 text-right">Incentive Earned</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {targets.map((t) => (
                  <tr key={t._id} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium">{t.period}</td>
                    <td className="px-4 py-3 text-right">
                      <div>₹{t.targetAmount?.toLocaleString('en-IN')}</div>
                      {!!t.carriedForwardAmount && (
                        <div className="mt-0.5 text-[11px] text-warning-text">
                          +₹{t.carriedForwardAmount.toLocaleString('en-IN')} carried from {t.carriedFromPeriod}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-success-text">
                      ₹{t.revenueGenerated?.toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 h-2 rounded-full bg-gray-200 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              t.achievedPercent >= 100
                                ? 'bg-success'
                                : t.achievedPercent >= 70
                                ? 'bg-warning'
                                : 'bg-danger'
                            }`}
                            style={{ width: `${Math.min(100, t.achievedPercent)}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium">{t.achievedPercent?.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-purple-700">
                      ₹{t.incentiveAmount?.toLocaleString('en-IN')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
